import { useEffect, useRef, useState } from "react";
import { ArrowRight, Play, ChevronDown, Leaf, Award, Truck } from "lucide-react";

export default function Hero({ onExploreProducts }) {
  const heroRef = useRef(null);
  const contentRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    const handleScroll = () => {
      if (!heroRef.current || !contentRef.current) return;
      const scrollY = window.scrollY;
      const heroHeight = heroRef.current.offsetHeight;
      if (scrollY < heroHeight) {
        contentRef.current.style.opacity = String(1 - (scrollY / heroHeight) * 0.5);
        contentRef.current.style.transform = `translateY(${scrollY * 0.3}px)`;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background Image with Gradient Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1600279297442-fab4907d4773?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" 
          alt="Coconut palms" 
          className="w-full h-full object-cover scale-105"
          style={{ animation: loaded ? 'none' : 'none' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/75 to-[#0F172A]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/50 via-transparent to-transparent" />
      </div>

      {/* Animated Floating Particles */}
      <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div 
            key={i} 
            className="absolute rounded-full bg-white/10"
            style={{ 
              left: `${Math.random()*100}%`, 
              top: `${Math.random()*100}%`, 
              width: `${4 + Math.random()*8}px`,
              height: `${4 + Math.random()*8}px`,
              animationDelay: `${Math.random()*5}s`, 
              animationDuration: `${10+Math.random()*10}s`,
              animation: `float ${10+Math.random()*10}s ease-in-out infinite ${Math.random()*5}s`
            }} 
          />
        ))}
      </div>

      {/* Decorative Lines */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-coir to-transparent opacity-50" />
      <div className="absolute left-8 top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent hidden lg:block" />

      {/* Main Content */}
      <div ref={contentRef} className="container-custom relative z-20 py-24">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className={`inline-flex items-center gap-3 bg-white/5 backdrop-blur-md border border-white/10 px-5 py-2.5 mb-10 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-coir opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-coir" />
            </span>
            <span className="text-white/90 text-sm font-medium font-sans tracking-wide">Premium Coir Manufacturer Since 2006</span>
          </div>

          {/* Main Heading */}
          <h1 className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif text-white mb-2 tracking-tight leading-[0.9] transition-all duration-1000 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            COCONUT
          </h1>
          <h1 className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif mb-2 tracking-tight leading-[0.9] transition-all duration-1000 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-coir">FIBER</span>
            <span className="text-white/30 mx-4">&</span>
          </h1>
          <h1 className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif text-forest tracking-tight leading-[0.9] transition-all duration-1000 delay-400 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            COCO PEAT
          </h1>

          {/* Subtitle */}
          <p className={`text-lg md:text-xl lg:text-2xl text-white/80 mt-8 mb-12 max-w-2xl font-sans font-light leading-relaxed transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Premium coir products for agriculture & industry. Sustainable solutions crafted from India's finest coconuts.
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-wrap gap-4 mb-16 transition-all duration-1000 delay-600 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <button 
              onClick={onExploreProducts} 
              className="btn-accent text-base md:text-lg group flex items-center gap-3" 
              data-testid="explore-products-btn"
            >
              Explore Products
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button 
              onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })} 
              className="border-2 border-white/30 text-white hover:bg-white hover:text-navy text-base md:text-lg px-8 py-3.5 font-medium transition-all duration-300 flex items-center gap-3 backdrop-blur-sm hover:-translate-y-0.5" 
              data-testid="learn-more-btn"
            >
              <Play className="w-5 h-5" />
              Learn More
            </button>
          </div>

          {/* Feature Pills */}
          <div className={`flex flex-wrap gap-4 transition-all duration-1000 delay-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {[
              { icon: Award, text: "19+ Years Experience" },
              { icon: Leaf, text: "100% Eco-Friendly" },
              { icon: Truck, text: "Worldwide Delivery" },
            ].map((item, i) => (
              <div 
                key={i} 
                className="flex items-center gap-2.5 bg-white/5 backdrop-blur-sm border border-white/10 px-4 py-2.5 text-white/80 hover:bg-white/10 transition-all duration-300 cursor-default"
              >
                <item.icon className="w-4 h-4 text-coir" />
                <span className="text-sm font-sans font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Side Stats Panel (Desktop) */}
        <div className={`hidden xl:block absolute right-12 top-1/2 -translate-y-1/2 transition-all duration-1000 delay-800 ${loaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
          <div className="bg-white/5 backdrop-blur-md border border-white/10 p-6 space-y-6">
            {[
              { val: "19+", label: "Years" },
              { val: "5+", label: "Products" },
              { val: "100%", label: "Natural" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-bold text-white font-mono stat-number">{s.val}</p>
                <p className="text-xs text-white/50 font-sans uppercase tracking-wider mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
        <button 
          onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })} 
          className="text-white/40 hover:text-white transition-colors animate-bounce-subtle"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1440 120" fill="none" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="#F9FAFB"/>
        </svg>
      </div>
    </div>
  );
}
